import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../../store/AuthContext";
import { useCan } from "../../hooks/useCan";
import * as Icons from "lucide-react";
import { USE_MOCK } from "../../api/axiosClient";

const sections = [
  {
    label: "Operations",
    items: [
      {
        to: "/admin/dashboard",
        icon: "LayoutDashboard",
        label: "Dashboard",
      },
      {
        to: "/admin/sales",
        icon: "ReceiptText",
        label: "Sales",
      },
      {
        to: "/admin/restaurant-orders",
        icon: "ClipboardList",
        label: "Restaurant Orders",
      },
      {
        to: "/admin/liquor-orders",
        icon: "ClipboardCheck",
        label: "Liquor Orders",
        ownerAdminOnly: true,
      },
      {
        to: "/admin/sales/missed",
        icon: "FilePlus2",
        label: "Missed Sale Entry",
        adminOnly: true,
      },
      {
        to: "/admin/corrections",
        icon: "FileEdit",
        label: "Corrections",
        adminOnly: true,
      },
    ],
  },
  {
    label: "Master Data",
    items: [
      {
        to: "/admin/bottles/types",
        icon: "Tag",
        label: "Bottle Types",
        adminOnly: true,
      },
      {
        to: "/admin/bottles/companies",
        icon: "Building2",
        label: "Liquor Suppliers",
        adminOnly: true,
      },
      {
        to: "/admin/bottles/products",
        icon: "Wine",
        label: "Bottle Products",
        adminOnly: true,
      },
      {
        to: "/admin/liquor-catalog",
        icon: "PackageSearch",
        label: "Supplier Catalogue",
        adminOnly: true,
      },
      {
        to: "/admin/bites/categories",
        icon: "Layers",
        label: "F & B Categories",
        adminOnly: true,
      },
      {
        to: "/admin/bites/products",
        icon: "Utensils",
        label: "F & B Products",
        adminOnly: true,
      },
    ],
  },
  {
    label: "Inventory",
    items: [
      {
        to: "/admin/stock/batches",
        icon: "Boxes",
        label: "Stock Batches",
      },
      {
        to: "/admin/stock/adjustments",
        icon: "Wrench",
        label: "Adjustments",
        adminOnly: true,
      },
      {
        to: "/admin/stock/low-stock",
        icon: "AlertTriangle",
        label: "Low Stock",
      },
    ],
  },
  {
    label: "Finance",
    items: [
      {
        to: "/admin/cash-sessions",
        icon: "Wallet",
        label: "Cash Sessions",
        adminOnly: true,
      },
      {
        to: "/admin/reports",
        icon: "BarChart3",
        label: "Reports",
      },
      {
        to: "/admin/profit",
        icon: "TrendingUp",
        label: "Profit Dashboard",
      },
      {
        to: "/admin/cash-settlements",
        icon: "HandCoins",
        label: "Cash & Settlements",
      },
    ],
  },
  {
    label: "Administration",
    adminOnly: true,
    items: [
      {
        to: "/admin/users",
        icon: "Users",
        label: "Users",
      },
      {
        to: "/admin/notifications/recipients",
        icon: "BellRing",
        label: "Notification Recipients",
      },
      {
        to: "/admin/notifications/whatsapp",
        icon: "MessageCircle",
        label: "WhatsApp & Notifications",
      },
      {
        to: "/admin/notifications/logs",
        icon: "Inbox",
        label: "Notification Logs",
      },
      {
        to: "/admin/audit/logs",
        icon: "ShieldCheck",
        label: "Audit Logs",
      },
    ],
  },
];

const isMenuItemActive = (itemTo, pathname) => {
  if (itemTo === "/admin/sales") {
    return (
      pathname === "/admin/sales" ||
      /^\/admin\/sales\/\d+(\/.*)?$/.test(pathname)
    );
  }

  if (itemTo === "/admin/sales/missed") {
    return pathname === "/admin/sales/missed";
  }

  return pathname === itemTo || pathname.startsWith(`${itemTo}/`);
};

export const Sidebar = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const can = useCan();
  const location = useLocation();

  const visibleSections = sections
    .filter((section) => !section.adminOnly || can.seeAdministration)
    .map((section) => ({
      ...section,

      label:
        section.label === "Master Data" && !can.isAdmin
          ? "Products"
          : section.label,

      items: section.items.filter((item) => (!item.adminOnly || can.isAdmin) && (!item.ownerAdminOnly || can.viewAdmin)),
    }))
    .filter((section) => section.items.length > 0);

  return (
    <>
      {isOpen && (
        <button
          type="button"
          aria-label="Close navigation menu"
          onClick={onClose}
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
        />
      )}

      <aside
        className={`
          bms-sidebar
          fixed inset-y-0 left-0 z-50
          flex h-dvh w-64 flex-col
          transition-transform duration-300 ease-in-out

          lg:sticky lg:top-0 lg:z-auto lg:h-screen
          lg:translate-x-0 lg:shrink-0

          ${isOpen ? "translate-x-0" : "-translate-x-full"}
        `}
      >
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-5">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-gold-500 font-display font-bold text-navy-900">
              THA
            </div>

            <div>
              <div className="font-display font-bold tracking-tight text-white">
                POS Portal
              </div>

              <div className="text-[10px] uppercase tracking-widest text-white/60">
                {USE_MOCK ? "Demo Mode" : "Live Backend"}
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close navigation menu"
            className="grid h-9 w-9 place-items-center rounded-lg text-white/70 transition hover:bg-white/10 hover:text-white lg:hidden"
          >
            <Icons.X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 space-y-4 overflow-y-auto px-2 py-3">
          {visibleSections.map((section) => (
            <div key={section.label}>
              <div className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-widest text-white/45">
                {section.label}
              </div>

              {section.items.map((item) => {
                const Icon = Icons[item.icon] || Icons.Circle;
                const isActive = isMenuItemActive(
                  item.to,
                  location.pathname
                );

                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={onClose}
                    aria-current={isActive ? "page" : undefined}
                    className={`group flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                      isActive
                        ? "bg-white font-medium text-navy-900 shadow-sm"
                        : "text-white/75 hover:bg-white/10 hover:text-white"
                    }`}
                    data-testid={`nav-${item.to.replace(/\W+/g, "-")}`}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span>{item.label}</span>
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="border-t border-white/10 px-4 py-3 text-[11px] text-white/55">
          <div className="truncate font-medium text-white/75">
            {user?.fullName}
          </div>
          <div className="truncate">{user?.roleName}</div>
        </div>
      </aside>
    </>
  );
};
